import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Slider,
  IconButton,
} from '@mui/material';
import {
  Language,
} from '@mui/icons-material';
import BotCard from './BotCard';

// Both callers hold the distance as a one-element array ([40]), so compare by
// value — a fresh array with the same number must not count as a change.
const isSameDistance = (a, b) => {
  const toArray = (value) => (Array.isArray(value) ? value : [value]);
  const left = toArray(a);
  const right = toArray(b);
  return left.length === right.length && left.every((n, i) => n === right[i]);
};

const BotSelection = ({ bots, selectedBot, onBotSelect, botDistance, setBotDistance, selectedUserName, onBotViewButtonClick, selectedUserId,loadingBots }) => {
  // `botDistance` is a dependency of the getNearUsers effect in both callers
  // (BotOnlyModal and MUIBasedPortfolio), and that request takes ~4s in a dense
  // city. MUI's `onChange` fires on every pointer tick, so one drag used to queue
  // an aggregation per intermediate value — the client aborts the previous one,
  // the server keeps computing it. Only the committed value is lifted now; this
  // local copy exists so the thumb and the label still follow the drag.
  const [draftDistance, setDraftDistance] = useState(botDistance);

  // Resync when the parent changes it on its own — MUIBasedPortfolio resets to
  // [40] when a different member is selected. Compared by value so a re-render
  // that hands back an equal array doesn't interrupt a drag in progress.
  useEffect(() => {
    setDraftDistance((current) => (isSameDistance(current, botDistance) ? current : botDistance));
  }, [botDistance]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, border: '1px solid #ccc', p: 2, borderRadius: 2, }}>
      <Box>
        <Typography sx={{ fontSize: '16px', color: '#000', fontWeight: 600 }}>
          Select a Bot
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Choose a bot to initiate the conversation with {selectedUserName}
        </Typography>
      </Box>

      {/* Bot Distance Slider */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Bot Distance For {selectedUserName}
        </Typography>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'grey.50',
          px: 0,
          py: 0,
          borderRadius: 25,
        }}>
          <Language sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Slider
            value={draftDistance}
            onChange={(e, newValue) => setDraftDistance(newValue)}
            // Fires once on pointer-up / key-up. The guard keeps a click on the
            // track at the current position, or a drag that returns to where it
            // started, from refetching — the callers' effects key on reference,
            // so an equal-but-new array would otherwise trigger one.
            onChangeCommitted={(e, newValue) => {
              if (!isSameDistance(newValue, botDistance)) setBotDistance(newValue);
            }}
            min={0}
            max={100}
            sx={{
              overflow: 'hidden',
              width: 80, p: 0, height: '12px',
              color: 'warning.main',
              '& .MuiSlider-track': {
                bgcolor: 'warning.main',
                left: '0',
                top: "0",
                transform: 'translateX(-100%) !important',
                width: '100% !important',
              },
              '& .MuiSlider-rail': {
                bgcolor: 'grey.300'
              },
              '& .MuiSlider-thumb': {
                bgcolor: '#fff', width: '11px', height: '11px',
              },
              '& .MuiSlider-thumb:hover': { boxShadow: 'none', },
              '& .MuiSlider-thumb:after': { display: 'none' }
            }}
          />
          <Typography variant="body2" sx={{ fontFamily: 'Arimo, sans-serif', minWidth: 50 }}>
            {draftDistance} km
          </Typography>
        </Box>
      </Box>

      {/* Bot List */}
      {loadingBots ?
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box> :
        <>
          {Boolean(bots?.length)
            ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {bots.map((bot) => (
                <BotCard
                  key={bot?._id}
                  bot={bot}
                  selected={selectedBot?._id === bot._id}
                  onClick={onBotSelect}
                  onBotViewButtonClick={onBotViewButtonClick}
                  selectedUserId={selectedUserId}
                />
              ))}
            </Box>
            : <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No bot found within the specified range
              </Typography>
            </Box>
          }
        </> 
    }
    </Box>
  );
};

export default BotSelection;